// Shared validation for the adminChangeEmail flow. Pure — kept out of the
// -interface.ts file so it can be unit-tested without implying it's part of the wire contract.
//
// Everything here really is about that one screen. The canonical email-normalization
// rule it uses is NOT: that lives in `./email-normalization` and many other paths
// delegate to it, so an edit there is an edit to all of them.

import { normalizeEmail } from './email-normalization';

// True when a (trimmed, lowercased) email has the minimum syntactic shape we accept:
// a non-empty local part, an '@', and a non-empty domain part. Used on both client
// (pre-submit sanity check) and server (final guard) so the rule can't drift.
export function isAdminEmailShapeValid(email: string): boolean {
  if (!email.includes('@')) return false;
  if (email.startsWith('@') || email.endsWith('@')) return false;
  return true;
}

export type AdminChangeEmailPureValidation =
  | { error: string; normalizedNewEmail?: undefined }
  | { error?: undefined; normalizedNewEmail: string };

// Validate the subset of inputs that don't depend on the current (oldEmail) value.
// Server calls this before any I/O; client calls it for pre-submit sanity.
export function validatePureAdminChangeEmailInputs(
  params: { userId: string; newEmail: string },
): AdminChangeEmailPureValidation {
  // defensive: runtime callers may lie about declared types
  if (!params.userId || typeof params.userId !== 'string') {
    return { error: 'Missing userId' };
  }
  if (!params.newEmail || typeof params.newEmail !== 'string') {
    return { error: 'Missing newEmail' };
  }
  let normalizedNewEmail = normalizeEmail(params.newEmail);
  if (!isAdminEmailShapeValid(normalizedNewEmail)) {
    return { error: 'Invalid newEmail (must contain a non-empty local and domain part)' };
  }
  return { normalizedNewEmail };
}

// Validate the one oldEmail-dependent rule: the new email can't be identical to the
// current one after normalization. Expects an already-normalized newEmail so the
// comparison matches what the pure validator produced.
export function validateAdminChangeEmailOldEmailRule(
  normalizedNewEmail: string,
  oldEmail: string | undefined,
): { error?: string } {
  if (oldEmail && normalizeEmail(oldEmail) === normalizedNewEmail) {
    return { error: `New email is the same as the current email (${oldEmail}).` };
  }
  return {};
}
